import React from 'react';
import { useIntl } from 'react-intl';
import { profileSourceMoves, type ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { Segmented } from '../../../components/primitives/Segmented';
import { HeaderPositionFields } from '../../studio/spotifyHeader/HeaderPositionFields';
import { PROFILE_BANNER_TABS, type ProfileImageTool } from '../profileRoute';
import type { ProfileRoute } from '../useProfileRoute';
import { BannerTextPicker } from './BannerTextPicker';
import { ProfileSourceTiles } from './ProfileSourceTiles';
import { ProfileSourcePickers } from './ProfileSourcePickers';
import { ProfileVersions } from './ProfileVersions';
import { ProfileToolRailPart } from './ProfileToolRailPart';
import { TOOL_COPY } from './profileToolCopy';
import { useProfileTool } from './useProfileTool';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileToolScreenProps {
  accountId: number;
  profile: ArtistProfileResponse;
  tool: ProfileImageTool;
  route: ProfileRoute;
}

const TAB_LABELS = { youtubeBanner: t.tabYoutube, facebookCover: t.tabFacebook } as const;

/**
 * One image tool of the Profile (design b1 header, b2 banners, b3 artist photo, b4 press photos; d2
 * below 700 px with the preview first): the picture sources, the picker of the source, Position,
 * Versions, and the rail with the preview and "Make it". The banners tab lives in the URL.
 */
const ProfileToolScreenComponent: React.FC<ProfileToolScreenProps> = ({ accountId, profile, tool, route }) => {
  const intl = useIntl();
  const state = useProfileTool({ accountId, profile, tool, banner: route.banner });
  const { kind, draft, change } = state;
  const copy = TOOL_COPY[tool];
  const versionAsset = state.slot.current ?? state.slot.working ?? state.slot.failed;

  return (
    <div className="d-flex flex-column gap-3" data-testid={`profile-tool-${tool}`}>
      {tool === 'banners' && (
        <Segmented
          className="align-self-start"
          ariaLabel={intl.formatMessage(t.bannersName)}
          value={route.banner}
          onChange={route.setBanner}
          options={PROFILE_BANNER_TABS.map((tab) => ({ value: tab, label: intl.formatMessage(TAB_LABELS[tab]) }))}
        />
      )}
      <div className={styles.tool}>
        <div className={styles.toolMain}>
          <Panel>
            {tool === 'banners' && (
              <BannerTextPicker value={draft.bannerText} onChange={(bannerText) => change({ bannerText })} />
            )}
            <ProfileSourceTiles
              kind={kind}
              value={draft.source}
              colours={state.colours}
              grounds={state.tileGrounds}
              hint={intl.formatMessage(copy.pictureHint)}
              onChange={state.pickSource}
            />
            <div className="px-3 pb-3">
              <ProfileSourcePickers
                accountId={accountId}
                kind={kind}
                draft={draft}
                onChange={change}
                releases={state.releases}
                pictures={state.pictures}
                artist={state.artist}
                artistThemeId={profile.look.themeId}
                aiReleaseTitle={state.aiRelease?.title ?? null}
              />
            </div>
            {profileSourceMoves(kind, draft.source) && (
              <HeaderPositionFields
                focusX={draft.focusX}
                focusY={draft.focusY}
                showLeftRight
                hint={intl.formatMessage(copy.positionHint)}
                onChange={(patch) => change(patch)}
              />
            )}
            <ProfileVersions accountId={accountId} asset={versionAsset} />
          </Panel>
        </div>
        <ProfileToolRailPart profile={profile} tool={tool} state={state} route={route} />
      </div>
    </div>
  );
};

export const ProfileToolScreen = TypedMemo(ProfileToolScreenComponent);
